import { EN } from './en.js';

/**
 * L'italiano e l'inglese.
 *
 * Da Salerno e da Edison guardano lo stesso programma, e chi lo guarda
 * dall'America deve poterlo leggere in inglese — non solo le e-mail che
 * escono, tutto.
 *
 * **La chiave è la frase italiana.** Non un codice tipo
 * `ordini.elenco.titolo`: la frase stessa. Costa una cosa — se si cambia
 * l'italiano bisogna cambiare anche la chiave — e ne fa guadagnare tre:
 *
 *  - il codice resta leggibile, `t('Nuovo ordine')` si capisce senza andare a
 *    cercare cosa c'è dietro il codice;
 *  - una frase senza traduzione resta in italiano, che è brutto ma si legge;
 *    con i codici resterebbe `ordini.elenco.titolo` a video;
 *  - non c'è da inventare ottocento nomi, e nessuno litiga su come chiamarli.
 *
 * La cosa che si perde — accorgersi che una frase italiana è cambiata e la
 * traduzione no — la restituisce `pnpm testi`, che confronta le frasi usate
 * nel codice con quelle tradotte e dice cosa manca e cosa è avanzato.
 */

export type Lingua = 'it' | 'en';

const CHIAVE = 'lr-lingua';

function iniziale(): Lingua {
  const salvata = localStorage.getItem(CHIAVE);
  if (salvata === 'it' || salvata === 'en') return salvata;
  // in mancanza d'altro, quella del browser: chi apre il programma da Edison
  // ha il telefono in inglese, e non deve cercare l'interruttore
  return navigator.language.toLowerCase().startsWith('it') ? 'it' : 'en';
}

let corrente: Lingua = iniziale();

export function lingua(): Lingua {
  return corrente;
}

/**
 * Cambiare lingua ricarica la pagina.
 *
 * Sembra brutale ed è invece la scelta che tiene il resto semplice: le
 * intestazioni delle tabelle, le voci del menu e le etichette dei campi sono
 * scritte una volta sola quando il file viene caricato, non a ogni disegno.
 * Ricaricare è l'unico modo perché cambino davvero tutte — e una lingua si
 * cambia una volta l'anno, non dieci volte al giorno.
 */
export function cambiaLingua(nuova: Lingua): void {
  if (nuova === corrente) return;
  localStorage.setItem(CHIAVE, nuova);
  location.reload();
}

/** Da usare all'avvio, per far combaciare `<html lang>` con quello che si legge. */
export function applicaLingua(): void {
  document.documentElement.lang = corrente;
}

/**
 * La lingua del profilo, quando su questo computer non ne è stata scelta una.
 *
 * Chi entra da una postazione nuova ritrova la lingua che usa sempre, senza
 * andarla a cercare. Se invece qui una scelta è già stata fatta, vince quella:
 * è la più recente, ed è anche quella che è già finita sul profilo.
 */
export function allineaConProfilo(delProfilo: Lingua | null | undefined): void {
  if (!delProfilo || localStorage.getItem(CHIAVE)) return;
  localStorage.setItem(CHIAVE, delProfilo);
  if (delProfilo !== corrente) location.reload();
}

/**
 * La frase, nella lingua in uso.
 *
 * I buchi si riempiono con `{nome}`: `t('{n} ordini', { n: 12 })`. Servono
 * perché in inglese le parole vanno in un altro ordine, e spezzare la frase
 * in tre pezzi da concatenare produce traduzioni che nessuna lingua accetta.
 */
export function t(frase: string, valori?: Record<string, string | number>): string {
  let testo = corrente === 'it' ? frase : (EN[frase] ?? frase);

  if (import.meta.env.DEV && corrente === 'en' && !EN[frase]) {
    mancanti.add(frase);
  }

  if (valori) {
    for (const [nome, valore] of Object.entries(valori)) {
      testo = testo.replaceAll(`{${nome}}`, String(valore));
    }
  }
  return testo;
}

/**
 * Le frasi che in inglese non hanno traduzione, raccolte mentre si lavora.
 *
 * `pnpm testi` le trova leggendo i sorgenti, che è il controllo che vale;
 * questo serve a vederle mentre si sviluppa, senza aspettare il controllo.
 */
export const mancanti = new Set<string>();
if (import.meta.env.DEV) {
  (window as unknown as { fraseMancante: () => string[] }).fraseMancante = () => [...mancanti];
}

/** Il codice di lingua per le date e i numeri: «it-IT», «en-US». */
export function locale(): string {
  return corrente === 'it' ? 'it-IT' : 'en-US';
}

/**
 * Il nome di uno stato d'ordine.
 *
 * A database gli stati sono scritti in inglese — «3. Goods ready to be
 * shipped» — perché è così che li chiama Business Central, e il servizio li
 * traduce in italiano. Chi legge in inglese vuole quello originale: non c'è
 * niente da tradurre, c'è solo da non tradurre.
 */
export function statoOrdine(tradotto: string, originale?: string | null): string {
  return corrente === 'en' && originale ? originale : tradotto;
}
